import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlContainer,
  ControlValueAccessor,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DatePickerComponent } from '@components/controls/date-picker/date-picker.component';
import { DatesPickerCMS } from 'src/app/interfaces/search-widget-interface';
import { DEFAULT_MIN_DATE, TripType } from '@constants/home.constants';
import { DATE_FORMAT_DEFAULT } from '@constants/date-format.constants';
import { Store } from '@ngrx/store';
import {
  selectIsWeb,
  selectShouldUseMobileStyling,
} from '@selectors/app.selector';

import { LetDirective } from '@directives/app-let.directive';
import { CalendarDateCssClass } from '@models/requests/route-schedule-request.model';

@Component({
  selector: 'app-dates-picker',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DatePickerComponent,
    LetDirective,
  ],
  templateUrl: './dates-picker.component.html',
  styleUrls: ['./dates-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatesPickerComponent),
      multi: true,
    },
  ],
})
export class DatesPickerComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @Input() tripType: string;
  @Input() group = 0;
  @Input() minDate = DEFAULT_MIN_DATE;
  @Input() maxDate: Date | undefined;
  @Input() cmsEntries: DatesPickerCMS;
  @Input() departDates: Date[] | undefined;
  @Input() returnDates: Date[] | undefined;
  @Input() departDateClasses: CalendarDateCssClass[] | undefined;
  @Input() returnDateClasses: CalendarDateCssClass[] | undefined;
  @Output() departDateChange = new EventEmitter<Date>();

  value: string;
  changed: (value: string) => void;
  touched: () => void;

  datesFormGroup!: UntypedFormGroup | null;
  format = DATE_FORMAT_DEFAULT;
  onDestroy$ = new Subject<void>();
  readonly tripTypeEnum = TripType;

  isWeb$ = this.store.select(selectIsWeb);
  useMobileStyling$ = this.store.select(selectShouldUseMobileStyling);

  get departDateControl(): UntypedFormControl | null {
    if (!this.datesFormGroup) {
      console.warn('No form group in depart date control getter (date picker)');
      return null;
    }
    return this.datesFormGroup.get('departDate') as UntypedFormControl | null;
  }

  get returnDateControl(): UntypedFormControl | null {
    if (!this.datesFormGroup) {
      console.warn('No form group in return date control getter (date picker)');
      return null;
    }
    return this.datesFormGroup?.get('returnDate') as UntypedFormControl | null;
  }

  constructor(
    private controlContainer: ControlContainer,
    private store: Store,
  ) {}

  ngOnInit(): void {
    this.datesFormGroup = this.controlContainer
      .control as UntypedFormGroup | null;
    if (!this.departDateControl) {
      console.warn('No depart date control in date picker component on init');
      return;
    }
    this.departDateControl.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value: Date) => this.departDateChange.emit(value));
  }

  // ControlValueAccessor interface methods implementations
  /* istanbul ignore next */
  writeValue(value: string): void {
    this.value = value;
  }

  /* istanbul ignore next */
  registerOnChange(fn: () => void): void {
    this.changed = fn;
  }

  /* istanbul ignore next */
  registerOnTouched(fn: () => void): void {
    this.touched = fn;
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }
}
